import React, { useEffect, useMemo, useState } from 'react';

import { Editor, TinyMCE } from 'tinymce';
import ModalDialog from '../../../../../../organisms/ModalDialog/ModalDialog';
import { PluginContext } from './PluginContext';
import { useSonTranslation } from '../../../../../../../translation/useSonTranslation';

export type PluginProps = {
  name: string;
  instance: TinyMCE;
  Component: React.FunctionComponent;
  icon: string;
  shortcut: string;
  title: 'Linkchooser:title' | 'Editor:keyboardHelp';

  addAdditionalIcon?: { name: string; svg: string };
  additionalMenuCommands?: any[];
  additionalToolBarCommands?: any[];
};

export function Plugin({
  name,
  icon,
  instance,
  title,
  shortcut,
  Component,
  addAdditionalIcon,
  additionalMenuCommands,
  additionalToolBarCommands,
}: PluginProps) {
  const { t } = useSonTranslation('glossary');
  const [editor, setEditor] = useState<Editor | null>(null);
  const [modalOpened, setModalOpened] = useState(false);

  useEffect(() => {
    // PluginManager needs to add a function, not a arrow function to properly work!
    instance?.PluginManager.add(name, function (editorInstance) {
      if (addAdditionalIcon) {
        editorInstance.ui.registry.addIcon(addAdditionalIcon.name, addAdditionalIcon.svg);
      }

      if (shortcut) {
        editorInstance.shortcuts.add(shortcut, t(title), () => setModalOpened(true));
      }

      // Define the toolbar button
      editorInstance.ui.registry.addButton(name, {
        tooltip: t(title),
        icon,
        onAction: () => setModalOpened(!modalOpened),
      });

      // Define the menu item
      editorInstance.ui.registry.addMenuItem(name, {
        text: t(title),
        icon,
        onAction: () => setModalOpened(!modalOpened),
      });

      additionalMenuCommands?.forEach(command => {
        editorInstance.ui.registry.addMenuItem(command.name, {
          text: t(command.title),
          icon: command.icon,
          onAction: () => command.onAction(editorInstance),
          onSetup: command.onSetup(editorInstance),
        });
      });

      additionalToolBarCommands?.forEach(command => {
        editorInstance.ui.registry.addButton(command.name, {
          tooltip: t(command.title),
          icon: command.icon,
          onAction: () => command.onAction(editorInstance),
          onSetup: command.onSetup(editorInstance),
        });
      });

      setEditor(editorInstance);
    });
  }, []);

  const pluginValue = useMemo(
    () => ({
      editor,
      setModalOpened,
      focusEditor: () => editor?.focus(false),
    }),
    [editor]
  );

  if (Component && modalOpened) {
    return (
      <ModalDialog headline={t(title)} closeModal={() => setModalOpened(false)}>
        <PluginContext.Provider value={pluginValue}>
          <Component />
        </PluginContext.Provider>
      </ModalDialog>
    );
  }

  return null;
}
